import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { BadgeCheck, Grid3X3, MessageCircle, MapPin, LogOut, Coins, TrendingUp, TrendingDown, Clock, Plus, CreditCard, Check } from 'lucide-react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Coin packages for top-up
const coinPackages = [
    { id: 1, coins: 100, price: '$1.99', priceValue: 1.99 },
    { id: 2, coins: 500, price: '$4.99', priceValue: 4.99, popular: true },
    { id: 3, coins: 1000, price: '$8.99', priceValue: 8.99 },
];

const ProfilePage = () => {
    const { userId } = useParams();
    const { user: currentUser, logout, updateUser } = useAuth();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [posts, setPosts] = useState([]);
    const [bets, setBets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('posts');
    
    // Top-up modal state
    const [showTopUpModal, setShowTopUpModal] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [showPaymentConfirm, setShowPaymentConfirm] = useState(false);
    const [processingPayment, setProcessingPayment] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);

    const isOwnProfile = !userId || userId === currentUser?.id;

    useEffect(() => {
        fetchProfile();
    }, [userId, currentUser]);

    const fetchProfile = async () => {
        try {
            const targetId = userId || currentUser?.id;
            const [profileRes, postsRes, betsRes] = await Promise.all([
                axios.get(`${API}/users/${targetId}`),
                axios.get(`${API}/users/${targetId}/posts`),
                axios.get(`${API}/bets/user/${targetId}`)
            ]);
            setProfile(profileRes.data);
            setPosts(postsRes.data);
            setBets(betsRes.data);
        } catch (error) {
            console.error('Error fetching profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStartConversation = async () => {
        try {
            const response = await axios.post(`${API}/messages/conversations?other_user_id=${profile.id}`);
            navigate(`/chat/${response.data.id}`);
        } catch (error) {
            console.error('Error starting conversation:', error);
            toast.error('Failed to start conversation');
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
        toast.success('Logged out successfully');
    };

    const handleSelectPackage = (pkg) => {
        setSelectedPackage(pkg);
        setShowPaymentConfirm(true);
    };

    const handleProcessPayment = async () => {
        setProcessingPayment(true);
        
        try {
            // Call backend to persist the top-up
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API}/users/me/topup`,
                { amount: selectedPackage.coins },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const newCoins = response.data.coins;
            
            // Update local state with the persisted value
            setProfile(prev => ({ ...prev, coins: newCoins }));
            
            // Also update the user context if it's the current user
            if (currentUser) {
                updateUser({ ...currentUser, coins: newCoins });
            }
            
            setProcessingPayment(false);
            setPaymentSuccess(true);
            
            // Reset after showing success
            setTimeout(() => {
                setPaymentSuccess(false);
                setShowPaymentConfirm(false);
                setShowTopUpModal(false);
                setSelectedPackage(null);
                toast.success(`${selectedPackage.coins} coins added to your wallet!`);
            }, 1500);
        } catch (error) {
            console.error('Top-up failed:', error);
            setProcessingPayment(false);
            toast.error(error.response?.data?.detail || 'Failed to top up coins. Please try again.');
        }
    };

    const closeTopUpModal = () => {
        setShowTopUpModal(false);
        setShowPaymentConfirm(false);
        setSelectedPackage(null);
        setPaymentSuccess(false);
    };

    const getBetStatusBadge = (status) => {
        switch (status) {
            case 'won':
                return (
                    <span className="flex items-center gap-1 text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full text-xs font-medium">
                        <TrendingUp className="w-3 h-3" />
                        Won
                    </span>
                );
            case 'lost':
                return (
                    <span className="flex items-center gap-1 text-red-400 bg-red-400/10 px-2 py-0.5 rounded-full text-xs font-medium">
                        <TrendingDown className="w-3 h-3" />
                        Lost
                    </span>
                );
            case 'pending':
                return (
                    <span className="flex items-center gap-1 text-yellow-400 bg-yellow-400/10 px-2 py-0.5 rounded-full text-xs font-medium">
                        <Clock className="w-3 h-3" />
                        Pending
                    </span>
                );
            default:
                return null;
        }
    };

    const calculateBetStats = () => {
        const totalBets = bets.length;
        const wonBets = bets.filter(b => b.status === 'won').length;
        const totalProfit = bets.reduce((acc, b) => acc + b.profit, 0);
        return { totalBets, wonBets, totalProfit };
    };

    const betStats = calculateBetStats();

    if (loading) {
        return (
            <div className="h-full bg-black flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="h-full bg-black flex items-center justify-center">
                <p className="text-white/60">User not found</p>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col bg-black" data-testid="profile-page">
            {/* Header */}
            <div className="flex-shrink-0 glass border-b border-white/10">
                <div className="max-w-md mx-auto flex items-center justify-between px-4 h-14">
                    <h1 className="font-heading text-xl font-bold text-white">
                        {isOwnProfile ? 'Profile' : profile.username}
                    </h1>
                    {isOwnProfile && (
                        <button
                            onClick={handleLogout}
                            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
                            data-testid="logout-button"
                        >
                            <LogOut className="w-5 h-5 text-white" />
                        </button>
                    )}
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
                {/* Profile Info */}
                <div className="max-w-md mx-auto px-4 py-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex flex-col items-center"
                    >
                        {/* Avatar */}
                        <div className="relative mb-4">
                            <Avatar className="w-24 h-24 border-4 border-card-bg">
                                <AvatarImage src={profile.avatar} alt={profile.username} />
                                <AvatarFallback className="bg-primary text-white text-2xl">
                                    {profile.username?.charAt(0).toUpperCase()}
                                </AvatarFallback>
                            </Avatar>
                            {profile.is_verified && (
                                <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-black rounded-full flex items-center justify-center">
                                    <BadgeCheck className="w-6 h-6 text-primary fill-primary" />
                                </div>
                            )}
                        </div>

                        {/* Username */}
                        <div className="flex items-center gap-2 mb-1">
                            <h2 className="text-white text-xl font-semibold">{profile.username}</h2>
                        </div>

                        {/* Coins Display with Top Up Button */}
                        {isOwnProfile && (
                            <div className="flex items-center gap-2 mb-3">
                                <div className="flex items-center gap-1.5 bg-yellow-500/20 px-3 py-1.5 rounded-full">
                                    <Coins className="w-4 h-4 text-yellow-400" />
                                    <span className="text-yellow-400 text-sm font-bold">{profile.coins || 1000} Coins</span>
                                </div>
                                <button
                                    onClick={() => setShowTopUpModal(true)}
                                    className="w-8 h-8 bg-primary hover:bg-primary-hover rounded-full flex items-center justify-center transition-colors"
                                    data-testid="top-up-button"
                                >
                                    <Plus className="w-4 h-4 text-white" />
                                </button>
                            </div>
                        )}

                        {/* Role & Position Badge */}
                        <div className="flex items-center gap-2 mb-3">
                            {profile.role && (
                                <span className="text-xs bg-primary/20 text-primary px-3 py-1 rounded-full capitalize">
                                    {profile.role}
                                </span>
                            )}
                            {profile.position && (
                                <span className="text-xs bg-white/10 text-white/80 px-3 py-1 rounded-full">
                                    {profile.position}
                                </span>
                            )}
                        </div>

                        {/* Region */}
                        {profile.region && (
                            <div className="flex items-center gap-1 text-white/60 text-sm mb-3">
                                <MapPin className="w-4 h-4" />
                                <span>{profile.region}</span>
                            </div>
                        )}

                        {/* Bio */}
                        {profile.bio && (
                            <p className="text-white/80 text-center text-sm mb-4 max-w-xs">
                                {profile.bio}
                            </p>
                        )}

                        {/* Stats */}
                        <div className="flex items-center gap-8 mb-6">
                            <div className="text-center">
                                <p className="font-heading text-2xl font-bold text-white">{profile.posts_count}</p>
                                <p className="text-white/60 text-xs uppercase">Posts</p>
                            </div>
                            <div className="text-center">
                                <p className="font-heading text-2xl font-bold text-white">{profile.followers_count?.toLocaleString()}</p>
                                <p className="text-white/60 text-xs uppercase">Followers</p>
                            </div>
                            <div className="text-center">
                                <p className="font-heading text-2xl font-bold text-white">{profile.following_count?.toLocaleString()}</p>
                                <p className="text-white/60 text-xs uppercase">Following</p>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        {!isOwnProfile && (
                            <div className="flex gap-3 w-full">
                                <Button
                                    className="flex-1 bg-primary hover:bg-primary-hover text-white font-semibold rounded-full"
                                    data-testid="follow-button"
                                >
                                    Follow
                                </Button>
                                <Button
                                    onClick={handleStartConversation}
                                    variant="outline"
                                    className="flex-1 border-border bg-surface text-white hover:bg-white/10 rounded-full"
                                    data-testid="message-button"
                                >
                                    <MessageCircle className="w-4 h-4 mr-2" />
                                    Message
                                </Button>
                            </div>
                        )}
                    </motion.div>
                </div>

                {/* Tabs */}
                <div className="max-w-md mx-auto px-4 pb-8">
                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                        <TabsList className="w-full bg-surface border border-border mb-4">
                            <TabsTrigger 
                                value="posts" 
                                className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-white"
                                data-testid="posts-tab"
                            >
                                <Grid3X3 className="w-4 h-4 mr-2" />
                                Posts
                            </TabsTrigger>
                            <TabsTrigger 
                                value="bets" 
                                className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-white"
                                data-testid="bets-tab"
                            >
                                <Coins className="w-4 h-4 mr-2" />
                                Bet History
                            </TabsTrigger>
                        </TabsList>

                        {/* Posts Tab */}
                        <TabsContent value="posts">
                            {posts.length === 0 ? (
                                <div className="py-12 text-center">
                                    <p className="text-white/60">No posts yet</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-3 gap-1" data-testid="posts-grid">
                                    {posts.map((post) => (
                                        <motion.div
                                            key={post.id}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="aspect-square bg-surface rounded overflow-hidden"
                                        >
                                            <img
                                                src={post.media_url}
                                                alt={post.caption}
                                                className="w-full h-full object-cover hover:opacity-80 transition-opacity cursor-pointer"
                                            />
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>

                        {/* Bets Tab */}
                        <TabsContent value="bets">
                            {/* Bet Stats Summary */}
                            <div className="grid grid-cols-3 gap-3 mb-4">
                                <div className="bg-surface border border-border rounded-xl p-3 text-center">
                                    <p className="text-white/60 text-xs">Total Bets</p>
                                    <p className="font-heading text-xl font-bold text-white">{betStats.totalBets}</p>
                                </div>
                                <div className="bg-surface border border-border rounded-xl p-3 text-center">
                                    <p className="text-white/60 text-xs">Won</p>
                                    <p className="font-heading text-xl font-bold text-green-400">{betStats.wonBets}</p>
                                </div>
                                <div className="bg-surface border border-border rounded-xl p-3 text-center">
                                    <p className="text-white/60 text-xs">Net Profit</p>
                                    <p className={`font-heading text-xl font-bold ${betStats.totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        {betStats.totalProfit >= 0 ? '+' : ''}{betStats.totalProfit}
                                    </p>
                                </div>
                            </div>

                            {bets.length === 0 ? (
                                <div className="py-12 text-center">
                                    <Coins className="w-12 h-12 text-white/20 mx-auto mb-3" />
                                    <p className="text-white/60">No bets yet</p>
                                    <p className="text-white/40 text-sm mt-1">Place your first bet in a Live Match Room!</p>
                                </div>
                            ) : (
                                <div className="space-y-3" data-testid="bets-list">
                                    {bets.map((bet, index) => (
                                        <motion.div
                                            key={bet.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className="bg-card-bg border border-border rounded-xl p-4"
                                            data-testid={`bet-${bet.id}`}
                                        >
                                            <div className="flex items-start justify-between mb-2">
                                                <div>
                                                    <p className="text-white font-medium text-sm">{bet.match_name}</p>
                                                    <p className="text-white/50 text-xs mt-0.5">
                                                        Selection: <span className="text-primary">{bet.selection_name}</span>
                                                    </p>
                                                </div>
                                                {getBetStatusBadge(bet.status)}
                                            </div>
                                            
                                            <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-border">
                                                <div>
                                                    <p className="text-white/40 text-[10px] uppercase">Amount</p>
                                                    <p className="text-white text-sm font-medium">{bet.amount}</p>
                                                </div>
                                                <div>
                                                    <p className="text-white/40 text-[10px] uppercase">Odds</p>
                                                    <p className="text-primary text-sm font-medium">{bet.odds}</p>
                                                </div>
                                                <div>
                                                    <p className="text-white/40 text-[10px] uppercase">Pot. Win</p>
                                                    <p className="text-white text-sm font-medium">{bet.potential_win}</p>
                                                </div>
                                                <div>
                                                    <p className="text-white/40 text-[10px] uppercase">Profit</p>
                                                    <p className={`text-sm font-bold ${
                                                        bet.profit > 0 ? 'text-green-400' : bet.profit < 0 ? 'text-red-400' : 'text-white/50'
                                                    }`}>
                                                        {bet.profit > 0 ? '+' : ''}{bet.profit}
                                                    </p>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>
                </div>
            </div>

            {/* Top Up Modal */}
            <Dialog open={showTopUpModal} onOpenChange={closeTopUpModal}>
                <DialogContent className="bg-card-bg border-border w-[calc(100%-2rem)] max-w-sm rounded-xl" data-testid="top-up-modal" aria-describedby="top-up-description">
                    <AnimatePresence mode="wait">
                        {!showPaymentConfirm ? (
                            <motion.div
                                key="packages"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                            >
                                <DialogHeader>
                                    <DialogTitle className="text-white font-heading text-2xl flex items-center gap-2">
                                        <Coins className="w-6 h-6 text-yellow-400" />
                                        Buy Coins
                                    </DialogTitle>
                                </DialogHeader>

                                <div className="space-y-3 mt-4">
                                    {coinPackages.map((pkg) => (
                                        <button
                                            key={pkg.id}
                                            onClick={() => handleSelectPackage(pkg)}
                                            className={`w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                                                pkg.popular 
                                                    ? 'border-primary bg-primary/10 hover:bg-primary/20' 
                                                    : 'border-border bg-surface hover:border-white/30'
                                            }`}
                                            data-testid={`package-${pkg.coins}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                                    pkg.popular ? 'bg-yellow-500/20' : 'bg-white/10'
                                                }`}>
                                                    <Coins className={`w-6 h-6 ${pkg.popular ? 'text-yellow-400' : 'text-white/60'}`} />
                                                </div>
                                                <div className="text-left">
                                                    <p className="text-white font-semibold">{pkg.coins} Coins</p>
                                                    {pkg.popular && (
                                                        <span className="text-xs text-primary font-medium">Most Popular</span>
                                                    )}
                                                </div>
                                            </div>
                                            <span className="text-white font-heading text-xl font-bold">{pkg.price}</span>
                                        </button>
                                    ))}
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="payment"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                            >
                                {!paymentSuccess ? (
                                    <>
                                        <DialogHeader>
                                            <DialogTitle className="text-white font-heading text-2xl">
                                                Confirm Payment
                                            </DialogTitle>
                                        </DialogHeader>

                                        <div className="mt-4 space-y-4">
                                            {/* Order Summary */}
                                            <div className="p-4 bg-surface rounded-xl">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-white/60">Item</span>
                                                    <span className="text-white font-medium">{selectedPackage?.coins} Coins</span>
                                                </div>
                                                <div className="flex items-center justify-between pt-2 border-t border-border">
                                                    <span className="text-white font-medium">Total</span>
                                                    <span className="text-primary font-heading text-xl font-bold">{selectedPackage?.price}</span>
                                                </div>
                                            </div>

                                            {/* Mock Payment Card */}
                                            <div className="p-4 bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl border border-white/10">
                                                <div className="flex items-center justify-between mb-3">
                                                    <CreditCard className="w-8 h-8 text-white/80" />
                                                    <span className="text-xs text-white/40 uppercase tracking-wider">Mastercard</span>
                                                </div>
                                                <p className="text-white font-mono text-lg tracking-wider">•••• •••• •••• 8844</p>
                                                <div className="flex items-center justify-between mt-3">
                                                    <span className="text-white/60 text-sm">John Doe</span>
                                                    <span className="text-white/60 text-sm">12/28</span>
                                                </div>
                                            </div>

                                            {/* Pay Button */}
                                            <Button
                                                onClick={handleProcessPayment}
                                                disabled={processingPayment}
                                                className="w-full bg-primary hover:bg-primary-hover text-white font-semibold h-12 rounded-full"
                                                data-testid="confirm-payment-button"
                                            >
                                                {processingPayment ? (
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                        Processing...
                                                    </div>
                                                ) : (
                                                    `Pay ${selectedPackage?.price}`
                                                )}
                                            </Button>

                                            <button
                                                onClick={() => setShowPaymentConfirm(false)}
                                                className="w-full text-white/60 text-sm hover:text-white transition-colors"
                                            >
                                                ← Choose different package
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <motion.div
                                        initial={{ scale: 0.8, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        className="py-8 text-center"
                                    >
                                        <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Check className="w-10 h-10 text-primary" />
                                        </div>
                                        <h3 className="text-white font-heading text-2xl font-bold mb-2">Success!</h3>
                                        <p className="text-white/60">
                                            {selectedPackage?.coins} coins added to your wallet
                                        </p>
                                    </motion.div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ProfilePage;
